import { create } from "zustand";

export interface CartItem {
  itemId: string;
  variationId: string;
  name: string;
  variationName: string;
  basePriceCents: number;
  originalPriceCents: number | null;
  qty: number;
}

interface CartState {
  items: CartItem[];
  addItem: (item: Omit<CartItem, "qty">) => void;
  removeItem: (variationId: string) => void;
  changeQty: (variationId: string, delta: number) => void;
  clearCart: () => void;
  cashSubtotal: () => number;
  cartSubtotal: () => number;
}

export const useCartStore = create<CartState>()((set, get) => ({
  items: [],

  addItem: (item) => {
    set((state) => {
      const existing = state.items.find((c) => c.variationId === item.variationId);
      if (existing) {
        return {
          items: state.items.map((c) =>
            c.variationId === item.variationId ? { ...c, qty: c.qty + 1 } : c,
          ),
        };
      }
      return { items: [...state.items, { ...item, qty: 1 }] };
    });
  },

  removeItem: (variationId) => {
    set((state) => ({
      items: state.items.filter((c) => c.variationId !== variationId),
    }));
  },

  changeQty: (variationId, delta) => {
    set((state) => {
      const updated = state.items
        .map((c) =>
          c.variationId === variationId ? { ...c, qty: c.qty + delta } : c,
        )
        .filter((c) => c.qty > 0);
      return { items: updated };
    });
  },

  clearCart: () => set({ items: [] }),

  cashSubtotal: () => {
    const { items } = get();
    return items.reduce((sum, c) => {
      const price = c.originalPriceCents ?? c.basePriceCents;
      return sum + price * c.qty;
    }, 0);
  },

  cartSubtotal: () => {
    const { items } = get();
    return items.reduce((sum, c) => sum + c.basePriceCents * c.qty, 0);
  },
}));
